import { useState, FormEvent, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Heart, Gift, Sparkle } from 'lucide-react';
import gsap from 'gsap';
import { useAuth } from '../../context/AuthContext';
import { useAuthStore } from '../../store/authStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (heroRef.current) {
      gsap.fromTo(heroRef.current, { opacity: 0, x: -40 }, { opacity: 1, x: 0, duration: 0.7, ease: 'power3.out' });
    }
    if (formRef.current) {
      gsap.fromTo(formRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', delay: 0.2 });
    }
    const floaters = heroRef.current?.querySelectorAll('.floating-card');
    if (floaters) {
      gsap.to(floaters, { y: -12, duration: 3, ease: 'sine.inOut', yoyo: true, repeat: -1, stagger: 1 });
    }
  }, []);

  if (isLoading) return null;
  if (isAuthenticated) {
    return <Navigate to={user?.role === 'ADMIN' ? '/admin' : '/'} replace />;
  }

  const validate = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await login(formData.email, formData.password);
      const currentUser = useAuthStore.getState().user;
      navigate(currentUser?.role === 'ADMIN' ? '/admin' : from, { replace: true });
    } catch {
      // Error handled by AuthContext
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="min-h-screen flex bg-twinkle-canvas">
      <div ref={heroRef} className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-twinkle-sage/20 via-twinkle-canvas to-twinkle-rose/10 relative overflow-hidden items-center justify-center p-12">
        <div className="floating-card absolute top-20 left-16 w-20 h-28 bg-white rounded-2xl border border-twinkle-mist shadow-lg flex flex-col items-center justify-center p-3 rotate-6">
          <Heart size={20} className="text-twinkle-rose mb-2" />
          <span className="text-[10px] font-display text-twinkle-ink/60 text-center leading-tight">With Love</span>
        </div>

        <div className="floating-card absolute bottom-32 right-20 w-18 h-24 bg-white rounded-2xl border border-twinkle-mist shadow-lg flex flex-col items-center justify-center p-3 -rotate-4">
          <Gift size={18} className="text-twinkle-sage mb-2" />
          <span className="text-[10px] font-display text-twinkle-ink/60 text-center leading-tight">Happy Birthday</span>
        </div>

        <div className="floating-card absolute top-40 right-32 w-16 h-22 bg-white rounded-2xl border border-twinkle-mist shadow-lg flex flex-col items-center justify-center p-2 rotate-12">
          <Sparkle size={16} className="text-twinkle-mist mb-1" />
          <span className="text-[9px] font-display text-twinkle-ink/60 text-center leading-tight">Joy</span>
        </div>

        <div className="relative z-10 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-twinkle-rose/15 flex items-center justify-center mx-auto mb-6">
            <Heart size={28} className="text-twinkle-rose" />
          </div>
          <h2 className="font-display text-3xl font-bold text-twinkle-ink mb-4">
            Welcome to TwinkleHearts
          </h2>
          <p className="text-twinkle-ink/60 leading-relaxed mb-6">
            Send love that arrives. Beautiful greeting cards delivered with care across Sri Lanka — order via WhatsApp for a personal touch.
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-twinkle-ink/40">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-twinkle-mist" />
              1,200+ orders
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-twinkle-mist" />
              4.9 rating
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8 lg:hidden">
            <Link to="/" className="inline-flex items-center gap-2">
              <Heart size={28} className="text-twinkle-rose" />
              <span className="text-2xl font-display font-semibold text-twinkle-ink">
                Twinkle<span className="text-twinkle-rose">Hearts</span>
              </span>
            </Link>
          </div>

          <div ref={formRef} className="card rounded-2xl p-8">
            <div className="text-center mb-8">
              <h1 className="font-display text-2xl font-bold text-twinkle-ink">Welcome Back</h1>
              <p className="text-twinkle-ink/50 mt-1">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="label-text">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input-field ${errors.email ? 'input-field-error' : ''}`}
                  placeholder="you@example.com"
                />
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="password" className="label-text">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-field ${errors.password ? 'input-field-error' : ''}`}
                  placeholder="••••••••"
                />
                {errors.password && <p className="error-text">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 min-h-[44px]">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-twinkle-rose border-twinkle-mist rounded focus:ring-twinkle-rose"
                  />
                  <span className="text-sm text-twinkle-ink/70">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-twinkle-ink hover:text-twinkle-rose font-medium min-h-[44px] flex items-center">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-3 text-base disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-twinkle-mist"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 text-twinkle-ink/40 bg-white">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                disabled
                className="mt-4 w-full flex items-center justify-center gap-3 border border-twinkle-mist text-twinkle-ink/40 py-3 rounded-xl font-medium cursor-not-allowed opacity-60 min-h-[44px]"
                title="Google sign-in coming soon"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Sign in with Google
                <span className="text-[10px] text-twinkle-ink/30 uppercase tracking-wider">(Coming Soon)</span>
              </button>
            </div>

            <p className="mt-8 text-center text-sm text-twinkle-ink/50">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-twinkle-ink hover:text-twinkle-rose">
                Join our family
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}